import { useState } from "react";
import type { KullaniciItem } from "../../../../mocks/kullanicilar";
import { rolColors, rolIcons } from "../../../../mocks/kullanicilar";

interface Props {
  user: KullaniciItem & { kisaNot?: string; username?: string };
  locked?: boolean;
  onEdit: () => void;
  onToggleDurum: () => void;
  onPasswordReset: () => void;
  onDelete: () => void;
}

const fmtDate = (date?: string) =>
  date
    ? new Date(date).toLocaleDateString("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

function Initials({ name }: { name: string }) {
  const parts = name.trim().split(" ");
  const initials =
    parts.length >= 2
      ? parts[0][0] + parts[parts.length - 1][0]
      : parts[0].slice(0, 2);

  return <span className="font-bold text-sm uppercase">{initials.toUpperCase()}</span>;
}

const avatarBg: Record<string, string> = {
  Admin: "bg-red-100 text-red-600",
  Muhasebe: "bg-amber-100 text-amber-700",
  "Kesim Alanı": "bg-orange-100 text-orange-700",
  Taşıyıcı: "bg-sky-100 text-sky-700",
  Müşteri: "bg-farm-green-pale text-farm-green",
  "Sıra Operatörü": "bg-indigo-100 text-indigo-700",
  "Kesim Operatörü": "bg-rose-100 text-rose-700",
  "Parçalama Operatörü": "bg-violet-100 text-violet-700",
  "Tamamlama Operatörü": "bg-teal-100 text-teal-700",
};

export default function UserRow({
  user,
  locked = false,
  onEdit,
  onToggleDurum,
  onPasswordReset,
  onDelete,
}: Props) {
  const [showActions, setShowActions] = useState(false);
  const roleColor = rolColors[user.rol];
  const avBg = avatarBg[user.rol] ?? "bg-farm-earth-pale text-farm-earth";

  const actionBase =
    "w-8 h-8 flex items-center justify-center mx-auto rounded-lg transition-colors";
  const actionDisabled = "opacity-40 cursor-not-allowed";
  const actionEnabled = "cursor-pointer";

  return (
    <>
      <div
        className="hidden md:grid items-center border-b border-farm-border last:border-b-0 hover:bg-farm-cream/50 transition-colors"
        style={{
          gridTemplateColumns: "40px 190px 130px 110px 90px 1fr 70px 70px 70px",
        }}
      >
        <div className="px-2 py-2.5 flex justify-center">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${avBg}`}
          >
            <Initials name={user.adSoyad} />
          </div>
        </div>

        <div className="px-2 py-2.5 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-xs font-semibold text-farm-brown truncate">
              {user.adSoyad}
            </p>
            {locked && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-red-50 text-red-600 border border-red-200">
                <i className="ri-lock-line" />
                Sabit
              </span>
            )}
          </div>
          {user.username ? (
            <p className="text-[10px] text-farm-muted truncate">Kullanıcı adı: {user.username}</p>
          ) : user.email ? (
            <p className="text-[10px] text-farm-muted truncate">{user.email}</p>
          ) : user.rol === "Müşteri" && "kisaNot" in user && user.kisaNot ? (
            <p className="text-[10px] text-farm-muted truncate italic">{user.kisaNot}</p>
          ) : null}
        </div>

        <div className="px-2 py-2.5">
          <p className="text-xs text-farm-brown font-mono">{user.telefon}</p>
        </div>

        <div className="px-2 py-2.5">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${roleColor.bg} ${roleColor.text} ${roleColor.border}`}
          >
            <i className={`${rolIcons[user.rol]} text-xs`} />
            {user.rol}
          </span>
        </div>

        <div className="px-2 py-2.5">
          <button
            type="button"
            disabled={locked}
            onClick={onToggleDurum}
            title={locked ? "Bootstrap admin korunur" : "Durum değiştir"}
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${
              user.durum === "Aktif"
                ? "bg-farm-green-pale text-farm-green border-farm-green/20"
                : "bg-gray-100 text-gray-500 border-gray-200"
            } ${locked ? actionDisabled : `${actionEnabled} hover:bg-farm-green/20`}`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                user.durum === "Aktif" ? "bg-farm-green" : "bg-gray-400"
              }`}
            />
            {user.durum}
          </button>
        </div>

        <div className="px-2 py-2.5">
          <p className="text-[10px] text-farm-muted">
            {user.sonGiris ? fmtDate(user.sonGiris) : "Hiç giriş yapmadı"}
          </p>
        </div>

        <div className="px-2 py-2.5 text-center">
          <button
            type="button"
            disabled={locked}
            onClick={onPasswordReset}
            title={locked ? "Bootstrap admin korunur" : "Şifre Sıfırla"}
            className={`${actionBase} text-farm-muted hover:bg-farm-earth-pale hover:text-farm-earth ${
              locked ? actionDisabled : actionEnabled
            }`}
          >
            <i className="ri-lock-password-line text-sm" />
          </button>
        </div>

        <div className="px-2 py-2.5 text-center">
          <button
            type="button"
            disabled={locked}
            onClick={onEdit}
            title={locked ? "Bootstrap admin korunur" : "Düzenle"}
            className={`${actionBase} text-farm-muted hover:bg-farm-earth-pale hover:text-farm-earth ${
              locked ? actionDisabled : actionEnabled
            }`}
          >
            <i className="ri-pencil-line text-sm" />
          </button>
        </div>

        <div className="px-2 py-2.5 text-center">
          <button
            type="button"
            disabled={locked}
            onClick={onDelete}
            title={locked ? "Bootstrap admin korunur" : "Sil"}
            className={`${actionBase} text-red-500 hover:bg-red-50 ${
              locked ? actionDisabled : actionEnabled
            }`}
          >
            <i className="ri-delete-bin-line text-sm" />
          </button>
        </div>
      </div>

      <div className="md:hidden border-b border-farm-border last:border-b-0 px-3 py-3">
        <div className="flex items-start gap-2.5">
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${avBg}`}
          >
            <Initials name={user.adSoyad} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-xs font-semibold text-farm-brown">
                {user.adSoyad}
              </p>
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${roleColor.bg} ${roleColor.text} ${roleColor.border}`}
              >
                <i className={`${rolIcons[user.rol]} text-[10px]`} />
                {user.rol}
              </span>
              {locked && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200">
                  <i className="ri-lock-line text-[10px]" />
                  Sabit
                </span>
              )}
              <button
                type="button"
                disabled={locked}
                onClick={onToggleDurum}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${
                  user.durum === "Aktif"
                    ? "bg-farm-green-pale text-farm-green border-farm-green/20"
                    : "bg-gray-100 text-gray-500 border-gray-200"
                } ${locked ? actionDisabled : actionEnabled}`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    user.durum === "Aktif" ? "bg-farm-green" : "bg-gray-400"
                  }`}
                />
                {user.durum}
              </button>
            </div>

            <p className="text-[10px] text-farm-muted mt-0.5 font-mono">
              {user.telefon}
            </p>
            {user.username ? (
              <p className="text-[10px] text-farm-muted mt-0.5">Kullanıcı adı: {user.username}</p>
            ) : null}
            {user.email ? (
              <p className="text-[10px] text-farm-muted truncate">{user.email}</p>
            ) : user.rol === "Müşteri" && "kisaNot" in user && user.kisaNot ? (
              <p className="text-[10px] text-farm-muted truncate italic">{user.kisaNot}</p>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => setShowActions((state) => !state)}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-farm-muted hover:bg-farm-border transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-more-2-fill text-sm" />
          </button>
        </div>

        {showActions && (
          <div className="mt-2 flex flex-col gap-2 pl-11">
            {locked && (
              <div className="text-[10px] text-red-600 bg-red-50 border border-red-200 rounded-farm px-2.5 py-2">
                Bu hesap sistemde korumalı bootstrap admindir.
              </div>
            )}

            <button
              type="button"
              disabled={locked}
              onClick={() => {
                if (locked) return;
                onPasswordReset();
                setShowActions(false);
              }}
              className={`h-8 text-xs font-semibold border border-farm-border text-farm-muted rounded-farm bg-farm-cream hover:bg-farm-earth-pale hover:text-farm-earth transition-colors whitespace-nowrap ${
                locked ? actionDisabled : actionEnabled
              }`}
            >
              <i className="ri-lock-password-line mr-1" />
              Şifre Sıfırla
            </button>

            <button
              type="button"
              disabled={locked}
              onClick={() => {
                if (locked) return;
                onEdit();
                setShowActions(false);
              }}
              className={`h-8 text-xs font-semibold border border-farm-earth/20 text-farm-earth bg-farm-earth-pale rounded-farm hover:bg-farm-earth hover:text-white transition-colors whitespace-nowrap ${
                locked ? actionDisabled : actionEnabled
              }`}
            >
              <i className="ri-pencil-line mr-1" />
              Düzenle
            </button>

            <button
              type="button"
              disabled={locked}
              onClick={() => {
                if (locked) return;
                onDelete();
                setShowActions(false);
              }}
              className={`h-8 text-xs font-semibold border border-red-200 text-red-600 bg-red-50 rounded-farm hover:bg-red-100 transition-colors whitespace-nowrap ${
                locked ? actionDisabled : actionEnabled
              }`}
            >
              <i className="ri-delete-bin-line mr-1" />
              Sil
            </button>
          </div>
        )}
      </div>
    </>
  );
}